// client/src/components/configuracoes/fidelidade/FidelidadeConfigPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Save, Info, AlertCircle } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { useFidelidadeConfig, useSalvarFidelidadeConfig } from '../../../hooks/useFidelidade';
import { useTemplatesMensagem } from '../../../hooks/useTemplateMensagem';

const ToggleSwitch = ({ checked, onChange, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
        <div
            onClick={() => onChange(!checked)}
            style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
                background: checked ? 'var(--color-primary)' : '#d1d5db',
                transition: 'background 0.2s',
            }}
        >
            <div style={{
                position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                left: checked ? 22 : 2,
                transition: 'left 0.2s',
            }} />
        </div>
        <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{label}</span>
    </label>
);

const FidelidadeConfigPage = () => {
    const { data: config, isLoading, isError, error } = useFidelidadeConfig();
    const { data: templates = [], isLoading: templatesLoading } = useTemplatesMensagem();
    const salvarConfig = useSalvarFidelidadeConfig();

    const [form, setForm] = useState({ ativo: false, pontosPorReal: 1, valorPontoEmReais: 0.01, templateMensagemId: '' });

    useEffect(() => {
        if (config) {
            setForm({
                ativo: config.ativo ?? false,
                pontosPorReal: config.pontosPorReal ?? 1,
                valorPontoEmReais: config.valorPontoEmReais ?? 0.01,
                templateMensagemId: config.templateMensagem?._id || '',
            });
        }
    }, [config]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        salvarConfig.mutate({
            ativo: form.ativo,
            pontosPorReal: Number(form.pontosPorReal) || 0,
            valorPontoEmReais: Number(form.valorPontoEmReais) || 0,
            templateMensagemId: form.templateMensagemId || null,
        });
    }, [form, salvarConfig]);

    // Exemplo ao vivo, pra staff entender o efeito das taxas antes de salvar.
    const exemploPontos = Math.floor(100 * (Number(form.pontosPorReal) || 0));
    const exemploDesconto = (100 * (Number(form.valorPontoEmReais) || 0)).toFixed(2);

    if (isLoading) return null;

    if (isError) {
        return (
            <div className="page-management-container">
                <PageHeader icon={<Gift />} title="Fidelidade" subtitle="Configure o programa de pontos da sua conta" />
                <div className="table-empty-state">
                    <AlertCircle size={48} style={{ color: '#dc2626' }} />
                    <h3>Não foi possível carregar</h3>
                    <p>{error?.response?.data?.error || error?.response?.data?.message || 'Erro ao carregar a configuração de fidelidade.'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-management-container">
            <PageHeader icon={<Gift />} title="Fidelidade" subtitle="Configure o programa de pontos da sua conta" />
            <form onSubmit={handleSubmit}>
                <div className="content-panel">
                    <div className="form-section">
                        <h4 className="form-section-title">Programa de Fidelidade</h4>
                        <div className="form-group">
                            <ToggleSwitch
                                checked={form.ativo}
                                onChange={(v) => setForm((prev) => ({ ...prev, ativo: v }))}
                                label={form.ativo ? 'Programa ativo' : 'Programa desativado'}
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Como o cliente ganha pontos</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">Pontos por R$1 gasto</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    className="form-input"
                                    value={form.pontosPorReal}
                                    onChange={(e) => setForm((prev) => ({ ...prev, pontosPorReal: e.target.value }))}
                                />
                            </div>
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> Uma venda de R$ 100,00 vai gerar {exemploPontos} pontos.
                        </p>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Como o cliente troca pontos por desconto</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">Valor de 1 ponto em R$</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="form-input"
                                    value={form.valorPontoEmReais}
                                    onChange={(e) => setForm((prev) => ({ ...prev, valorPontoEmReais: e.target.value }))}
                                />
                            </div>
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> 100 pontos vão valer R$ {exemploDesconto} de desconto. Não afeta o resgate por prêmio do catálogo — esse usa o custo em pontos cadastrado em cada prêmio.
                        </p>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Aviso de pontos ganhos</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">Mensagem de "você ganhou pontos"</label>
                                <select
                                    className="form-input"
                                    value={form.templateMensagemId}
                                    onChange={(e) => setForm((prev) => ({ ...prev, templateMensagemId: e.target.value }))}
                                    disabled={templatesLoading}
                                >
                                    <option value="">Sem aviso automático (não selecionado)</option>
                                    {templates.map((t) => (
                                        <option key={t._id} value={t._id}>{t.nome} ({t.canal})</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <p className="form-hint" style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            Sem um template selecionado, os pontos continuam sendo creditados normalmente — só não avisa o cliente. Variáveis disponíveis: <code>{'{{cliente.primeiroNome}}'}</code>, <code>{'{{cliente.nomeCompleto}}'}</code>, <code>{'{{fidelidade.pontosGanhos}}'}</code>, <code>{'{{fidelidade.saldoTotal}}'}</code>, <code>{'{{fidelidade.valorGasto}}'}</code>, <code>{'{{empresa.name}}'}</code>.
                            Crie ou edite templates em Templates → Mensagens.
                        </p>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="action-button action-button-primary" disabled={salvarConfig.isPending}>
                            <Save size={18} className="icon" /> {salvarConfig.isPending ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default FidelidadeConfigPage;
